import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Select, Button, Spin, Empty, Divider, List, Avatar, Tooltip, Tag } from 'antd';
import { ImCancelCircle } from 'react-icons/im';
import { ExperimentOutlined, SearchOutlined, LinkOutlined, EnvironmentOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { BeatLoader } from "react-spinners";
import { getRadiologyExamById, assignRadiologyExamToFacility } from '../../../services/radiology_exam';
import { getAllFacilities } from '../../../services/facilities';
import './AssignRadiologyExamModal.css';

const { Option } = Select;

const validationSchema = Yup.object().shape({
  facilityId: Yup.string().required('Please select a facility')
});

const AssignRadiologyExamModal = ({ radiologyExamId, onClose, onAssignComplete }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingFacilities, setFetchingFacilities] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [radiologyExam, setRadiologyExam] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filteredFacilities, setFilteredFacilities] = useState([]);

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return "No address available";
    
    // If address is a string, return it directly
    if (typeof address === 'string') return address;
    
    // If address is an object, format it
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.area) parts.push(address.area);
    if (address.city) parts.push(address.city);
    
    return parts.length > 0 ? parts.join(', ') : "No address details";
  };

  // Format price range for display
  const formatPriceRange = (minPrice, maxPrice) => {
    if (!minPrice && !maxPrice) return "Price not specified";
    if (minPrice && !maxPrice) return `${minPrice} FCFA`;
    if (!minPrice && maxPrice) return `Up to ${maxPrice} FCFA`;
    return `${minPrice} - ${maxPrice} FCFA`;
  };

  // Check if facility already has this exam assigned
  const isExamAssigned = (facility) => {
    if (!facility || !facility.radiologyExams || !radiologyExamId) return false;

    // Check if radiologyExamId exists in facility.radiologyExams array
    return facility.radiologyExams.some(exam => 
      (typeof exam === 'string' && exam === radiologyExamId) || 
      (exam && exam.id === radiologyExamId)
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch the radiology exam details
        if (radiologyExamId) {
          const examResponse = await getRadiologyExamById(radiologyExamId);
          setRadiologyExam(examResponse.data || examResponse.data?.data);
        }

        // Fetch all facilities
        setFetchingFacilities(true);
        const facilitiesResponse = await getAllFacilities();
        setFacilities(facilitiesResponse?.data?.facilities || []);
        setFilteredFacilities(facilitiesResponse?.data?.facilities || []);
        setFetchingFacilities(false);
      } catch (error) {
        toast.error('Error fetching data');
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [radiologyExamId]);

  useEffect(() => {
    if (searchText && facilities.length > 0) {
      const filtered = facilities.filter(facility =>
        facility.facilityName?.toLowerCase().includes(searchText.toLowerCase()) ||
        (formatAddress(facility.address).toLowerCase().includes(searchText.toLowerCase()))
      );
      setFilteredFacilities(filtered);
    } else {
      setFilteredFacilities(facilities);
    }
  }, [searchText, facilities]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      await assignRadiologyExamToFacility(radiologyExamId, values.facilityId);
      onAssignComplete();
      toast.success('Radiology exam assigned to facility successfully');
    } catch (error) {
      console.error('Assignment error:', error);
      toast.error(error.message || 'Error assigning radiology exam to facility');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  if (loading) {
    return (
      <div className="modal-wrapper">
        <div className="modal-container">
          <div className="modal-content loading-content">
            <Spin size="large" />
          </div>
        </div>
        <div className="modal-overlay" onClick={onClose} />
      </div>
    );
  }

  return (
    <div className="modal-wrapper">
      <div className="modal-container assign-modal">
        <div className="modal-header">
          <h2>Assign Radiology Exam to Facility</h2>
          <ImCancelCircle 
            className="close-icon"
            onClick={onClose} 
            size={22} 
          />
        </div>

        <div className="modal-content">
          {radiologyExam && (
            <div className="exam-details">
              <div className="exam-header">
                <ExperimentOutlined className="exam-icon" />
                <div className="exam-title">
                  <h3>{radiologyExam.examName}</h3>
                  <span className="exam-category">{radiologyExam.category}</span>
                </div>
              </div>
              <Divider />
              <div className="exam-info">
                <div className="info-item">
                  <span className="info-label">Price Range:</span>
                  <span className="info-value">
                    {formatPriceRange(radiologyExam.minPrice, radiologyExam.maxPrice)}
                  </span>
                </div>
                {radiologyExam.description && (
                  <div className="info-item">
                    <span className="info-label">Description:</span>
                    <span className="info-value">{radiologyExam.description}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <Formik
            initialValues={{ facilityId: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleSubmit, setFieldValue, isSubmitting }) => (
              <Form className="form-container">
                <div className="facility-selection-section">
                  <div className="selection-header">
                    <h3>Select a Facility</h3>
                    <div className="assignment-legend">
                      <Tag color="green" icon={<CheckCircleOutlined />}>Already assigned</Tag>
                    </div>
                  </div>
                  
                  <div className="facility-search">
                    <Select
                      showSearch
                      placeholder="Search for a facility..."
                      optionFilterProp="children"
                      onChange={(value) => setFieldValue('facilityId', value)}
                      onSearch={handleSearch}
                      notFoundContent={fetchingFacilities ? <Spin size="small" /> : <Empty description="No facilities found" />}
                      value={values.facilityId}
                      className="facility-select"
                      dropdownClassName="facility-dropdown"
                      suffixIcon={<SearchOutlined />}
                      filterOption={false}
                      loading={fetchingFacilities}
                    >
                      {filteredFacilities.map(facility => {
                        const isAssigned = isExamAssigned(facility);
                        return (
                          <Option key={facility.id} value={facility.id} disabled={isAssigned}>
                            <div className={`facility-option ${isAssigned ? 'assigned-facility' : ''}`}>
                              <div className="facility-option-name">
                                {facility.facilityName}
                                {isAssigned && (
                                  <Tag color="green" className="assigned-tag">
                                    <CheckCircleOutlined /> Assigned
                                  </Tag>
                                )}
                              </div>
                              <div className="facility-option-address">
                                <EnvironmentOutlined /> {formatAddress(facility.address)}
                              </div>
                            </div>
                          </Option>
                        );
                      })}
                    </Select>
                    {errors.facilityId && touched.facilityId && (
                      <div className="error-message">{errors.facilityId}</div>
                    )}
                  </div>

                  {facilities.length > 0 && (
                    <div className="facility-list">
                      <List
                        itemLayout="horizontal"
                        dataSource={filteredFacilities.slice(0, 5)}
                        renderItem={facility => {
                          const isAssigned = isExamAssigned(facility);
                          return (
                            <Tooltip title={isAssigned ? "Exam already assigned to this facility" : ""}>
                              <List.Item
                                className={`facility-list-item ${values.facilityId === facility.id ? 'selected' : ''} ${isAssigned ? 'assigned-facility' : ''}`}
                                onClick={() => !isAssigned && setFieldValue('facilityId', facility.id)}
                              >
                                <List.Item.Meta
                                  avatar={
                                    <Avatar 
                                      icon={<EnvironmentOutlined />} 
                                      className="facility-avatar"
                                      style={{ 
                                        backgroundColor: isAssigned ? '#52c41a' : 
                                          facility.facilityType === 'hospital' ? '#ff4d4f' : 
                                          facility.facilityType === 'pharmacy' ? '#52c41a' : '#1890ff' 
                                      }}
                                    />
                                  }
                                  title={
                                    <div className="facility-title">
                                      {facility.facilityName}
                                      {isAssigned && (
                                        <Tag color="green" className="assigned-tag">
                                          <CheckCircleOutlined /> Assigned
                                        </Tag>
                                      )}
                                    </div>
                                  }
                                  description={
                                    <div className="facility-description">
                                      <span>{facility.facilityType}</span>
                                      <span> • {formatAddress(facility.address)}</span>
                                    </div>
                                  }
                                />
                              </List.Item>
                            </Tooltip>
                          );
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="button-container">
                  <Button 
                    onClick={onClose} 
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={!values.facilityId || isSubmitting}
                    className="submit-button"
                  >
                    Assign to Facility
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="modal-overlay" onClick={onClose} />
    </div>
  );
};

export default AssignRadiologyExamModal;